/**
 * Simplified Organization Query Utilities
 * 
 * Replaces complex withOrganizationScope with explicit, predictable patterns.
 * Each table type has its own clear query method.
 * 
 * Created: 2025-06-28
 * Purpose: Simplify and optimize organization-scoped database queries
 */

import { supabase } from './client';
import { createLogger } from '@/utils/logger/devLogger';

const logger = createLogger('OrgQueries');

export interface OrganizationQueryOptions {
  select?: string;
  orderBy?: { column: string; ascending?: boolean };
  limit?: number;
}

/**
 * Standard tables with direct organization_id column
 */
export const standardOrgQueries = {
  /**
   * Teams - direct organization_id relationship
   */
  teams: (orgId: string, options: OrganizationQueryOptions = {}) => {
    logger.debug('Querying teams for organization:', orgId);
    
    let query = supabase
      .from('teams')
      .select(options.select || '*')
      .eq('organization_id', orgId);
      
    if (options.orderBy) {
      query = query.order(options.orderBy.column, { ascending: options.orderBy.ascending ?? true });
    }
    
    if (options.limit) {
      query = query.limit(options.limit);
    }
    
    return query;
  },

  /**
   * Events - direct organization_id relationship with team join support
   */
  events: (orgId: string, options: OrganizationQueryOptions = {}) => {
    logger.debug('Querying events for organization:', orgId);
    
    let query = supabase
      .from('events')
      .select(options.select || '*, event_teams(team_id)')
      .eq('organization_id', orgId);
      
    if (options.orderBy) {
      query = query.order(options.orderBy.column, { ascending: options.orderBy.ascending ?? true });
    }
    
    if (options.limit) {
      query = query.limit(options.limit);
    }
    
    return query;
  },

  /**
   * Gym Locations - direct organization_id relationship
   */
  gymLocations: (orgId: string, options: OrganizationQueryOptions = {}) => {
    logger.debug('Querying gym locations for organization:', orgId);
    
    let query = supabase
      .from('gymlocations')
      .select(options.select || '*')
      .eq('organization_id', orgId);
      
    if (options.orderBy) {
      query = query.order(options.orderBy.column, { ascending: options.orderBy.ascending ?? true });
    }
    
    if (options.limit) {
      query = query.limit(options.limit);
    }
    
    return query;
  },

  /**
   * Organization Staff - direct organization_id relationship with auth_users join
   * ✅ UPDATED: Uses auth_users as source of truth for names
   *
   * Requirements Summary (Issue #538):
   * - Filters by status='active' so Pause/Deactivate (which flip
   *   organization_staff.status to 'inactive') automatically remove the
   *   coach from every downstream picker that uses this shared helper
   *   (Staff Schedules grid, team coach selectors, etc).
   */
  staff: (orgId: string, options: OrganizationQueryOptions = {}) => {
    logger.debug('Querying staff for organization:', orgId);
    
    // ✅ FIXED: Use explicit relationship to avoid JOIN ambiguity
    const defaultSelect = `
      id,
      employee_id,
      preferred_name,
      phone,
      employment_type,
      employment_status,
      position_title,
      hire_date,
      status,
      system_access_level,
      primary_location_id,
      organization_id,
      auth_user_id,
      created_at,
      updated_at,
      org_staff_roles(role_name),
      auth_users!auth_user_id(
        first_name,
        last_name,
        email,
        avatar_url
      )
    `;
    
    let query = supabase
      .from('organization_staff')
      .select(options.select || defaultSelect)
      .eq('organization_id', orgId)
      .eq('status', 'active'); // Issue #538: exclude paused/deactivated.

    if (options.orderBy) {
      query = query.order(options.orderBy.column, { ascending: options.orderBy.ascending ?? true });
    }

    if (options.limit) {
      query = query.limit(options.limit);
    }

    return query;
  },

  /**
   * Program Staff Assignments - optimized for bulk loading
   *
   * Requirements Summary:
   * - Include class section and schedule date bounds for staff calendar expansion
   * - Include schedule active state so inactive class schedules stay hidden
   */
  programStaff: (orgId: string, options: OrganizationQueryOptions = {}) => {
    logger.debug('Querying program staff for organization:', orgId);
    
    const defaultSelect = `
      auth_user_id,
      program_id,
      role,
      role_title,
      programs!inner(
        id,
        name,
        program_type,
        status,
        organization_id,
        classes(id),
        class_sections!class_sections_class_program_id_fkey(
          id,
          instructor_id,
          session_start_date,
          session_end_date,
          schedules:class_schedules!class_schedules_class_section_id_fkey(
            day_of_week, start_time, end_time, effective_date, end_date, instructor_id, location_id, is_active,
            location:gymlocations!class_schedules_location_id_fkey(id, name)
          )
        )
      )
    `;
    
    let query = supabase
      .from('program_staff')
      .select(options.select || defaultSelect)
      .eq('organization_id', orgId)
      .eq('status', 'active');
      
    if (options.limit) {
      query = query.limit(options.limit);
    }
    
    return query;
  },

  /**
   * Private Lesson Bookings - organization scoped
   */
  privateLessonBookings: (orgId: string, options: OrganizationQueryOptions = {}) => {
    logger.debug('Querying private lesson bookings for organization:', orgId);
    
    const defaultSelect = `
      id,
      coach_id,
      private_lesson_program_id,
      session_duration,
      lesson_date,
      start_time,
      end_time,
      status,
      gym_location_id,
      athletes(first_name, last_name),
      organization_staff!private_lesson_bookings_coach_id_fkey(auth_user_id),
      gymlocations!gym_location_id(name)
    `;
    
    let query = supabase
      .from('private_lesson_bookings')
      .select(options.select || defaultSelect)
      .eq('organization_id', orgId);
      
    if (options.limit) {
      query = query.limit(options.limit);
    }
    
    return query;
  },
};

/**
 * Special case tables that use join tables for organization relationships
 */
export const joinTableOrgQueries = {
  /**
   * Athletes - through athlete_organizations junction table
   * This is the most common special case
   */
  athletes: (orgId: string, options: OrganizationQueryOptions = {}) => {
    logger.debug('Querying athletes for organization:', orgId);
    
    // ✅ SIMPLIFIED: Use athlete_organizations as base table to avoid join syntax issues
    let query = supabase
      .from('athlete_organizations')
      .select('athlete_id, status, created_at, athletes (*)')
      .eq('organization_id', orgId);
      
    // Note: Cannot order by nested athlete fields when querying from athlete_organizations
    // Ordering will be handled in the service layer after data transformation
    if (options.orderBy && options.orderBy.column === 'created_at') {
      // Only allow ordering by athlete_organizations fields
      query = query.order(options.orderBy.column, { ascending: options.orderBy.ascending ?? true });
    }
    
    if (options.limit) {
      query = query.limit(options.limit);
    }
    
    return query;
  },

  /**
   * Athletes with full auxiliary data - optimized single query
   */
  athletesComplete: (orgId: string, athleteId?: string) => {
    logger.debug('Querying complete athlete data for organization:', orgId);
    
    const baseQuery = supabase
      .from('athletes')
      // athlete_medical_admin_info carries the insurance/physician fields that
      // used to sit on athlete_medical_info. It is embedded alongside rather than
      // instead of: RLS returns it only to owner/admin, so for coaching staff it
      // simply comes back empty and the rest of the query is unaffected.
      .select('*, athlete_organizations!inner(organization_id, status), athlete_emergency_contacts(*), athlete_medical_info(*), athlete_medical_admin_info(*), athlete_skill_levels(*), athlete_uniform_sizes(*), athlete_authorized_pickups(*), athlete_addresses(*)')
      .eq('athlete_organizations.organization_id', orgId);
      
    if (athleteId) {
      return baseQuery.eq('id', athleteId).single();
    }
    
    return baseQuery;
  },

  /**
   * Team Athletes - through teams relationship with proper joins
   */
  teamAthletes: (orgId: string, teamId?: string, options: OrganizationQueryOptions = {}) => {
    logger.debug('Querying team athletes for organization:', orgId);
    
    const select = options.select || `
      team_athletes.*,
      athletes(
        id,
        first_name,
        last_name,
        date_of_birth,
        email,
        phone,
        profile_image_url
      ),
      teams(
        name,
        description
      )
    `;
    
    let query = supabase
      .from('team_athletes')
      .select(select)
      .eq('teams.organization_id', orgId)
      .eq('status', 'active');
      
    if (teamId) {
      query = query.eq('team_id', teamId);
    }
      
    if (options.orderBy) {
      query = query.order(options.orderBy.column, { ascending: options.orderBy.ascending ?? true });
    }
    
    if (options.limit) {
      query = query.limit(options.limit);
    }
    
    return query;
  },

  /**
   * Parents - through parent_organizations junction table
   */
  parents: (orgId: string, options: OrganizationQueryOptions = {}) => {
    logger.debug('Querying parents for organization:', orgId);
    
    const select = options.select || `
      parents.*,
      parent_organizations.status,
      parent_organizations.created_at as joined_at
    `;
    
    let query = supabase
      .from('parents')
      .select(`${select}, parent_organizations!inner(organization_id, status)`)
      .eq('parent_organizations.organization_id', orgId)
      .eq('parent_organizations.status', 'active');
      
    if (options.orderBy) {
      query = query.order(options.orderBy.column, { ascending: options.orderBy.ascending ?? true });
    }
    
    if (options.limit) {
      query = query.limit(options.limit);
    }
    
    return query;
  },

  /**
   * Parents with full auxiliary data - optimized single query
   */
  parentsComplete: (orgId: string, parentId?: string) => {
    logger.debug('Querying complete parent data for organization:', orgId);
    
    const baseQuery = supabase
      .from('parents')
      .select('*, parent_organizations!inner(organization_id, status), parent_athletes(*), parent_emergency_contacts(*), parent_addresses(*)')
      .eq('parent_organizations.organization_id', orgId)
      .eq('parent_organizations.status', 'active');
      
    if (parentId) {
      return baseQuery.eq('id', parentId).single();
    }
    
    return baseQuery;
  }
};

/**
 * Verification helpers - simplified versions of complex verification functions
 */
export const orgVerification = {
  /**
   * Simple athlete organization membership check
   * Includes both active and pending enrollments since staff should see pending athletes
   */
  isAthleteInOrganization: async (athleteId: string, orgId: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase
        .from('athlete_organizations')
        .select('athlete_id')
        .eq('athlete_id', athleteId)
        .eq('organization_id', orgId)
        .in('status', ['active', 'pending'])
        .maybeSingle();
        
      if (error) {
        logger.warn('Error checking athlete membership:', error);
        return false;
      }
      
      return !!data;
    } catch (error) {
      logger.warn('Exception checking athlete membership:', error);
      return false;
    }
  },

  /**
   * Simple team ownership verification
   */
  isTeamInOrganization: async (teamId: string, orgId: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase
        .from('teams')
        .select('id')
        .eq('id', teamId)
        .eq('organization_id', orgId)
        .maybeSingle();
        
      if (error) {
        logger.warn('Error checking team ownership:', error);
        return false;
      }
      
      return !!data;
    } catch (error) {
      logger.warn('Exception checking team ownership:', error);
      return false;
    }
  },

  /**
   * Simple parent organization membership check
   */
  isParentInOrganization: async (parentId: string, orgId: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase
        .from('parent_organizations')
        .select('parent_id')
        .eq('parent_id', parentId)
        .eq('organization_id', orgId)
        .eq('status', 'active')
        .maybeSingle();
        
      if (error) {
        logger.warn('Error checking parent membership:', error);
        return false;
      }
      
      return !!data;
    } catch (error) {
      logger.warn('Exception checking parent membership:', error);
      return false;
    }
  },

  /**
   * Simple staff ownership verification
   */
  isStaffInOrganization: async (staffId: string, orgId: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase
        .from('organization_staff')
        .select('id')
        .eq('id', staffId)
        .eq('organization_id', orgId)
        .maybeSingle();
        
      if (error) {
        logger.warn('Error checking staff ownership:', error);
        return false;
      }
      
      return !!data;
    } catch (error) {
      logger.warn('Exception checking staff ownership:', error);
      return false;
    }
  },

  /**
   * Simple event ownership verification
   */
  isEventInOrganization: async (eventId: string, orgId: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase
        .from('events')
        .select('id')
        .eq('id', eventId)
        .eq('organization_id', orgId)
        .maybeSingle();
        
      if (error) {
        logger.warn('Error checking event ownership:', error);
        return false;
      }
      
      return !!data;
    } catch (error) {
      logger.warn('Exception checking event ownership:', error);
      return false;
    }
  }
};

/**
 * Convenience export for the most common queries
 */
export const orgQueries = {
  ...standardOrgQueries,
  ...joinTableOrgQueries,
  verify: orgVerification
};

export default orgQueries;
